import { Mixin } from 'mixwith';
import { NodeDataBuilder } from 'Editor/services/DataManager';
import DOMElementFactory from 'Editor/services/DOMUtilities/DOMElementFactory/DOMElementFactory';
import { Logger } from '_common/services';
import { ELEMENTS } from 'Editor/services/consts';
import { EditorSelectionUtils } from 'Editor/services/_Common/Selection';
import { EditorDOMElements, EditorDOMUtils } from 'Editor/services/_Common/DOM';

export default Mixin(
  (superclass) =>
    class EnterHandler extends superclass {
      destroy() {
        super.destroy();
      }

      /**
       * handle enter on multi selection
       * @param {ActionContext} actionContext
       */
      handleEnterOnMultiSelection(actionContext) {
        if (this.joinSelectionContent(actionContext)) {
          const selection = EditorSelectionUtils.getSelection();
          const anchorNode = selection.anchorNode;
          const anchorOffset = selection.anchorOffset;
          const level0Node = EditorDOMUtils.findFirstLevelChildNode(this.page, anchorNode);

          this.handleEnterOnCollapsedSelection(actionContext, level0Node, anchorNode, anchorOffset);
        }
      }

      /**
       * selects a function to handle enter based on baseNode
       * @param {ActionContext} actionContext
       * @param {Node} baseNode
       * @param {Node} anchorNode
       * @param {*} anchorOffset
       */
      handleEnterOnCollapsedSelection(actionContext, baseNode, anchorNode, anchorOffset) {
        if (!baseNode || EditorDOMElements.isNodeContainerElement(anchorNode)) {
          // if baseNode is undefined try to fix selection
          if (EditorSelectionUtils.fixSelection()) {
            const selection = EditorSelectionUtils.getSelection();
            baseNode = EditorDOMUtils.findFirstLevelChildNode(this.page, selection.anchorNode);
            anchorNode = selection.anchorNode;
            anchorOffset = selection.anchorOffset;
          }
        }

        if (baseNode) {
          if (anchorNode) {
            if (
              baseNode.tagName === ELEMENTS.ParagraphElement.TAG &&
              this.dataManager.numbering.isListElement(baseNode.id)
            ) {
              // SELECTION IS A LIST and we want to handle a specific case before handling on text element
              this.handleEnterOnListElement(actionContext, baseNode, anchorNode, anchorOffset);
            } else if (EditorDOMUtils.isClosestTextElementEditable(baseNode)) {
              // SELECTION IS A DEFAULT TEXT ELEMENT
              this.handleEnterOnTextElement(actionContext, baseNode, anchorNode, anchorOffset);
            } else if (!EditorDOMUtils.isClosestBlockNodeEditable(baseNode)) {
              // SELECTION IS A NON-EDITABLE ELEMENT
              this.handleNonEditableElementEnter(actionContext, baseNode);
            } else if (baseNode.tagName === ELEMENTS.FigureElement.TAG) {
              // SELECTION IS A FIGURE
              this.handleEnterOnFigureElement(actionContext, baseNode, anchorNode, anchorOffset);
            } else if (baseNode.tagName === ELEMENTS.TableElement.TAG) {
              // SELECTION IS A TABLE
              this.handleEnterOnTableElement(actionContext, baseNode, anchorNode, anchorOffset);
            } else if (EditorDOMElements.isNodeContainerElement(baseNode)) {
              this.handleEnterOnContainerElement(actionContext, baseNode, anchorNode, anchorOffset);
            }
          }
        }
      }

      /**
       * handle enter on a non-editable element
       * @param {ActionContext} actionContext
       */
      handleNonEditableElementEnter(actionContext, level0Node) {
        // enter
        try {
          if (this.hasEditPermissions()) {
            const contentEditable = level0Node && level0Node.selectableContent;

            if (contentEditable) {
              let parentNode = level0Node.parentNode;
              let refNode = level0Node;
              let parent_id;
              if (
                parentNode.tagName === ELEMENTS.TrackDeleteElement.TAG ||
                parentNode.tagName === ELEMENTS.TrackInsertElement.TAG
              ) {
                refNode = parentNode;
                parentNode = this.page;
                parent_id = parentNode.id;
              } else if (level0Node.hasAttribute('enclosed_element')) {
                // approved and read only elements
                refNode = document.getElementById(level0Node.getAttribute('enclosed_element'));
                parent_id = refNode.getAttribute('parent_id');
              } else {
                parent_id = parentNode.id;
              }

              const data = NodeDataBuilder.buildParagraph({
                parent_id: parent_id,
              });

              if (EditorSelectionUtils.isSelectionAtEnd(contentEditable)) {
                this.inserBlockNodeOperation(actionContext, data, refNode, 'AFTER');

                EditorSelectionUtils.setCaret(EditorDOMUtils.getBlockNode(data.id));
                return;
              }

              if (EditorSelectionUtils.isSelectionAtStart(contentEditable)) {
                this.inserBlockNodeOperation(actionContext, data, refNode, 'Before');
                return;
              }
            }
          }
        } catch (e) {
          Logger.captureException(e);
        }
      }

      /**
       * handle enter event on list elements~
       * @param {ActionContext} actionContext
       * @param {Node} anchorNode
       */
      handleEnterOnListElement(actionContext, listElement, anchorNode, anchorOffset) {
        if (
          listElement.firstChild === listElement.lastChild &&
          (this.isAddParagraphMarker(listElement.firstChild) ||
            this.isDeleteParagraphMarker(listElement.firstChild)) &&
          !this.dataManager.numbering.isBlockInOutlineList(listElement.id)
        ) {
          const listElements = [];
          const pageNode = EditorDOMUtils.getContentContainer(listElement);
          if (listElement.parentNode !== pageNode) {
            const level0 = EditorDOMUtils.findFirstLevelChildNode(pageNode, listElement);

            listElements.push({
              blockId: level0.id,
              id: listElement.id,
            });
          } else {
            listElements.push({
              blockId: listElement.id,
              id: listElement.id,
            });
          }
          this.dataManager.numbering.outdentNodes(listElements, true);
          if (
            listElement.nextSibling &&
            this.dataManager.numbering.isBulletListElement(listElement.nextSibling.id)
          ) {
            this.dataManager.numbering.restartListNumbering(
              actionContext,
              listElement.nextSibling.id,
            );
          }
        } else if (
          EditorDOMUtils.isEmptyElement(listElement) &&
          !this.dataManager.numbering.isBlockInOutlineList(listElement.id)
        ) {
          this.dataManager.numbering.removeBlocksFromList([listElement.id]);
          actionContext.jsonChanges = true;
        } else {
          this.handleEnterOnTextElement(actionContext, listElement, anchorNode, anchorOffset);

          // const listId = this.persistenceManager.getListIdFromBlock(before.id);
          // const listLevel = this.persistenceManager.getListLevelFromBlock(before.id);
          // this.persistenceManager.addBlocksToList(
          //   actionContext,
          //   [after.id],
          //   listId,
          //   listLevel,
          //   before.id,
          // );
          // if (!before.previousSibling && before.textContent === '') {
          //   this.persistenceManager.removeBlocksFromList([before.id]);
          //   actionContext.jsonChanges = true;
          // }
        }
      }

      /**
       * handle enter event on a figure element
       * @param {ActionContext} actionContext
       * @param {Node} figureNode
       * @param {Node} anchorNode
       */
      handleEnterOnFigureElement(actionContext, figureNode, anchorNode, anchorOffset) {
        const closest = EditorDOMUtils.closest(anchorNode, [
          'IMAGE-ELEMENT',
          ELEMENTS.FigureElement.TAG,
        ]);
        if (closest) {
          if (
            closest?.tagName === 'IMAGE-ELEMENT' ||
            closest?.tagName === ELEMENTS.FigureElement.TAG
          ) {
            let parentNode = figureNode.parentNode;
            let refNode = figureNode;
            if (
              parentNode.tagName === ELEMENTS.TrackDeleteElement.TAG ||
              parentNode.tagName === ELEMENTS.TrackInsertElement.TAG
            ) {
              refNode = parentNode;
              parentNode = this.page;
            }

            if (
              (closest === anchorNode && anchorOffset === 0) ||
              (closest !== anchorNode && !anchorNode.previousSibling)
            ) {
              const closestContainer = EditorDOMUtils.closest(
                refNode,
                EditorDOMUtils.MULTI_BLOCK_CONTAINER_ELEMENTS,
              );
              if (closestContainer) {
                const p = DOMElementFactory.createNewParagraphElement();
                EditorDOMUtils.insertNodeAfter(refNode.parentNode, p, refNode);
                actionContext.addChangeAddedNode(p);
                EditorSelectionUtils.setCaret(p, 'INSIDE_START');
              } else {
                const data = NodeDataBuilder.buildParagraph({
                  parent_id: parentNode.id,
                });

                this.inserBlockNodeOperation(actionContext, data, refNode, 'BEFORE');
                EditorSelectionUtils.setCaret(EditorDOMUtils.getBlockNode(data.id));
              }
            } else if (
              (closest === anchorNode && anchorOffset === closest.childNodes.length) ||
              (closest !== anchorNode && !anchorNode.nextSibling)
            ) {
              const closestContainer = EditorDOMUtils.closest(
                refNode,
                EditorDOMElements.MULTI_BLOCK_CONTAINER_ELEMENTS,
              );
              if (closestContainer) {
                const p = DOMElementFactory.createNewParagraphElement();
                EditorDOMUtils.insertNodeAfter(refNode.parentNode, p, refNode);
                actionContext.addChangeAddedNode(p);
                EditorSelectionUtils.setCaret(p, 'INSIDE_START');
              } else {
                const data = NodeDataBuilder.buildParagraph({
                  parent_id: parentNode.id,
                });

                this.inserBlockNodeOperation(actionContext, data, refNode, 'AFTER');
                EditorSelectionUtils.setCaret(EditorDOMUtils.getBlockNode(data.id));
              }
            }
          }
        }
      }

      /**
       * handle enter event on table element
       * @param {ActionContext} actionContext
       * @param {Node} anchorNode
       */
      handleEnterOnTableElement(actionContext, baseNode, anchorNode, anchorOffset) {
        let parentNode = baseNode.parentNode;
        let refNode = baseNode;
        if (
          parentNode.tagName === ELEMENTS.TrackDeleteElement.TAG ||
          parentNode.tagName === ELEMENTS.TrackInsertElement.TAG
        ) {
          refNode = parentNode;
          parentNode = this.page;
        }

        const handleAnchorNodeTableCell = (pNode, rNode) => {
          const p = DOMElementFactory.createNewParagraphElement();

          if (anchorOffset === 0) {
            EditorDOMUtils.insertNodeBefore(pNode, p, rNode);
            actionContext.addChangeAddedNode(p);
            EditorSelectionUtils.setCaret(p, 'INSIDE_END');
          } else if (anchorOffset === anchorNode.childNodes.length) {
            EditorDOMUtils.insertNodeAfter(pNode, p, rNode);
            actionContext.addChangeAddedNode(p);
            EditorSelectionUtils.setCaret(p, 'INSIDE_END');
          }
        };

        const closest = EditorDOMUtils.closest(anchorNode, [ELEMENTS.TableCellElement.TAG]);
        if (closest && !closest.hasAttribute('lock')) {
          if (closest.tagName === ELEMENTS.TableCellElement.TAG) {
            const row = closest.parentNode;
            const body = row.parentNode;
            const table = body.parentNode;

            if (
              !table.caption &&
              Array.from(row.childNodes).indexOf(closest) === 0 &&
              Array.from(body.childNodes).indexOf(row) === 0 &&
              EditorSelectionUtils.isSelectionAtStart(closest.firstChild) &&
              (refNode.previousSibling == null ||
                refNode.previousSibling.tagName !== ELEMENTS.ParagraphElement.TAG)
            ) {
              // if no caption and selection it's at start of the first cell and there is no previous sibling
              const data = NodeDataBuilder.buildParagraph({
                parent_id: parentNode.id,
              });

              this.inserBlockNodeOperation(actionContext, data, refNode, 'BEFORE');
              const node = EditorDOMUtils.getNode(data.id);
              EditorSelectionUtils.setCaret(node, 'INSIDE_START');
            } else {
              const tdLevel0Node = EditorDOMUtils.findFirstLevelChildNode(closest, anchorNode);
              if (tdLevel0Node) {
                if (tdLevel0Node.tagName !== ELEMENTS.TableElement.TAG) {
                  this.handleEnterOnCollapsedSelection(
                    actionContext,
                    tdLevel0Node,
                    anchorNode,
                    anchorOffset,
                  );
                } else if (tdLevel0Node !== baseNode) {
                  handleAnchorNodeTableCell(closest, tdLevel0Node);
                }
              }
            }
          }
        } else if (anchorNode.tagName === ELEMENTS.TableElement.TAG) {
          const paragraphData = NodeDataBuilder.buildData({
            type: ELEMENTS.ParagraphElement.ELEMENT_TYPE,
            parent_id: parentNode.id,
          });
          if (anchorOffset === 0) {
            this.inserBlockNodeOperation(actionContext, paragraphData, refNode, 'BEFORE');
          } else if (anchorOffset === anchorNode.childNodes.length) {
            this.inserBlockNodeOperation(actionContext, paragraphData, refNode, 'AFTER');
            const node = EditorDOMUtils.getNode(paragraphData.id);
            EditorSelectionUtils.setCaret(node, 'INSIDE_START');
          }
        }
      }

      handleEnterOnContainerElement(actionContext, baseNode, anchorNode, anchorOffset) {
        if (EditorSelectionUtils.isSelectionAtStart(baseNode)) {
          const data = NodeDataBuilder.buildParagraph({
            parent_id: baseNode.parentNode.id,
          });

          this.inserBlockNodeOperation(actionContext, data, baseNode, 'BEFORE');
          return;
        } else if (EditorSelectionUtils.isSelectionAtEnd(baseNode)) {
          const data = NodeDataBuilder.buildParagraph({
            parent_id: baseNode.parentNode.id,
          });

          this.inserBlockNodeOperation(actionContext, data, baseNode, 'AFTER');

          EditorSelectionUtils.setCaret(EditorDOMUtils.getBlockNode(data.id));
          return;
        } else {
          if (EditorSelectionUtils.fixSelection()) {
            const selection = EditorSelectionUtils.getSelection();
            anchorNode = selection.anchorNode;
            anchorOffset = selection.anchorOffset;
          }
        }

        const subLevel0Node = EditorDOMUtils.findFirstLevelChildNode(baseNode, anchorNode);
        if (subLevel0Node) {
          this.handleEnterOnCollapsedSelection(
            actionContext,
            subLevel0Node,
            anchorNode,
            anchorOffset,
          );
        }
      }

      /**
       * handle enter event on a default text element
       * @param {ActionContext} actionContext
       */
      handleEnterOnTextElement(actionContext) {
        EditorSelectionUtils.fixCollapsedTextSelection();

        return this.splitSelectionContent(actionContext);
      }
    },
);
